// What the account has — the figures the account menu shows, however it is
// composed (devVariant.ts). The counts are what the session already holds (no
// request). The rewards reading — rank, points, the GRAM available — is the
// Rewards page's own GET for the primary wallet, made when the menu opens for
// a signed-in account. A figure that is not there is not invented: every read
// below gives way to "unknown", and "unknown" is drawn as a dash or not at all.

import type { MeSnapshot } from '../../api/userland'
import { type RewardsSummary, useRewardsSummary } from '../../hooks/useRewardsSummary'
import { formatCompactExact } from '../../lib/format'
import { useTradingSession } from '../../wallet/TradingSessionContext'
import { useWallets } from '../../wallet/WalletsContext'
import { TIERS, type Tier } from '../rewards/program'

export type AccountFigures = {
  /** False for a menu with no signed-in account behind it: nothing is shown. */
  known: boolean
  /** Tokens held by the wallets the balance sums; null until that is known. */
  tokens: number | null
  /** The account's live (not archived) wallets. */
  wallets: number
  /** The primary wallet's rewards reading; null when there is none. */
  rewards: RewardsSummary | null
  rewardsLoading: boolean
}

export function useAccountFigures(me: MeSnapshot | null): AccountFigures {
  const { active, wallets } = useWallets()
  const { selectedPortfolio, portfolio } = useTradingSession()
  const rewards = useRewardsSummary(me ? (active?.address ?? null) : null)
  return {
    known: me !== null,
    tokens: (selectedPortfolio ?? portfolio?.portfolio)?.holdings_count ?? null,
    wallets: (wallets ?? []).filter((wallet) => !wallet.archived).length,
    rewards: me ? rewards.summary : null,
    rewardsLoading: me !== null && rewards.status === 'loading',
  }
}

export const DASH = '—'

export const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`

/** The rank as the API spelled it, trimmed; '' when there is no reading. */
export const rankOf = (figures: AccountFigures) => figures.rewards?.rank.trim() ?? ''

/** The tier of the rewards programme a rank names, when it names one. */
export const tierOf = (rank: string): Tier | undefined =>
  TIERS.find((tier) => tier.name.toLowerCase() === rank.toLowerCase())

/** "Silver" — the tier's own name, else the rank with a capital. */
export function rankName(rank: string): string {
  return tierOf(rank)?.name ?? `${rank.charAt(0).toUpperCase()}${rank.slice(1)}`
}

/** Points are a count, not an amount: no decimals (as the Rewards page prints
 *  them). Null when there is no reading. */
export function pointsText(summary: RewardsSummary | null): string | null {
  if (!summary) return null
  const whole = summary.points_total.split('.')[0] || '0'
  return formatCompactExact(`${whole}.0`).replace(/\.00$/, '')
}

/** "1.25K points" / "1 point". */
export function pointsLabel(summary: RewardsSummary): string {
  const whole = summary.points_total.split('.')[0] || '0'
  return `${pointsText(summary)} ${whole === '1' ? 'point' : 'points'}`
}

/** The GRAM the Rewards page calls "Rewards Available": its figure, or null
 *  when there is no reading or nothing to claim. */
export function availableGram(summary: RewardsSummary | null): string | null {
  const total = summary?.total_rewards_claimable_gram ?? ''
  return /[1-9]/.test(total) ? formatCompactExact(total) : null
}
